/**
 * Renderery dla różnych typów kolumn
 * Centralizacja logiki renderowania komórek tabeli
 */

import React from 'react';
import { X } from 'react-bootstrap-icons';
import * as BootstrapIcons from 'react-bootstrap-icons';
import { format, parseISO, isSameDay, addDays, isBefore } from 'date-fns';
import { CheckIcon } from 'assets/icons';
import { Trans } from 'react-i18next';
import classnames from 'classnames';
import { Avatar } from '@mui/material';

import { theme } from 'theme';
import type { ThemeWithApp } from 'theme/useAppTheme';
import { ColumnAction } from 'plugins/components/controls';
import { Link } from 'components/controls';
import StatusPill from 'components/wise/WiseTable/components/StatusPill';
import GradeIndicator from 'components/wise/WiseTable/components/GradeIndicator';
import WisePriorityComponent from 'components/wise/WiseTable/components/WisePriorityComponent';
import { IWiseTableAction, IWiseTableColumn } from 'components/wise/types';

import styles from 'theme/components/wise/WiseTable/utils/columnRenderers.module.scss';

/**
 * Renderer dla kolumny typu 'link' – wyświetla tekst jako link do podanej ścieżki
 */
export const renderLinkColumn = (
  item: any,
  fieldSymbol: string,
  _columnAction?: IWiseTableAction,
  _refetch?: () => void,
  column?: IWiseTableColumn,
  requestParams?: object
): React.ReactNode => {
  const path = column?.link_path;
  const idField = column?.link_id_field ?? 'id';
  const rowId = item?.[idField];
  const text = item?.[fieldSymbol];

  if (!path || rowId == null || rowId === '') {
    return <span>{text}</span>;
  }

  let href = path;
  const params = requestParams as { clientId?: number; id?: string | number } | undefined;
  let routeClientId = params?.clientId ?? params?.id;
  if (routeClientId == null && column?.link_client_id_field) {
    routeClientId = item?.[column.link_client_id_field];
  }

  if (path.includes(':offerId')) {
    if (routeClientId != null && path.includes(':id')) {
      href = href.replace(':id', String(routeClientId));
    }
    href = href.replace(':offerId', String(rowId));
  } else {
    href = href.replace(':id', String(rowId));
  }

  return (
    <Link to={href} className={styles.linkColumn}>
      {text ?? ''}
    </Link>
  );
};

/**
 * Renderer dla kolumny typu 'text' lub 'string'
 */
export const renderTextColumn = (
  item: any,
  fieldSymbol: string,
  columnAction?: IWiseTableAction,
  refetch?: () => void
): React.ReactNode => {
  if (columnAction) {
    return (
      <div className="link">
        {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
        <ColumnAction action={columnAction} item={item} refetch={refetch || (() => {})} />
      </div>
    );
  }

  return <span>{item?.[fieldSymbol]}</span>;
};

/**
 * Renderer dla kolumny typu 'status' – wyświetla StatusPill.
 */
export const renderStatusColumn = (
  item: any,
  fieldSymbol: string,
  _columnAction?: IWiseTableAction,
  _refetch?: () => void,
  column?: IWiseTableColumn
): React.ReactNode => {
  const statusValue = item?.[fieldSymbol] ?? '';
  const label = column?.label_field_symbol ? item?.[column.label_field_symbol] : undefined;
  return <StatusPill status={String(statusValue)} label={label} />;
};

/**
 * Renderer dla kolumny typu 'image'
 */
export const renderImageColumn = (item: any, fieldSymbol: string): React.ReactNode => {
  const src = item?.[fieldSymbol];

  if (!src) {
    return null;
  }

  return (
    <div style={{ width: 150, height: 150, flexShrink: 0 }}>
      <img
        src={src}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
    </div>
  );
};

/**
 * Renderer dla kolumny typu 'address'
 */
export const renderAddressColumn = (item: any): React.ReactNode => {
  const address = item?.address;
  if (address) {
    return `${address.street || ''}, ${address.postal_code || ''} ${address.city || ''}`.trim();
  }
  return '';
};

/**
 * Renderer dla kolumny typu 'boolean'
 */
export const renderBooleanColumn = (item: any, fieldSymbol: string): React.ReactNode => {
  const appTheme = theme as ThemeWithApp;
  return item?.[fieldSymbol] ? <CheckIcon /> : <X fontSize={28} color={appTheme.palette.app.orangeHover} />;
};

/**
 * Renderer dla kolumny typu 'grade' - wyświetla kółko/kropkę w zależności od stopnia wiarygodności
 */
export const renderGradeColumn = (item: any, fieldSymbol: string): React.ReactNode => {
  const gradeValue = item?.[fieldSymbol] || '';
  return <GradeIndicator grade={gradeValue} />;
};

/**
 * Renderer dla kolumny typu 'priority' - wyświetla ikonę priorytetu (0 - normal, 1 - urgent)
 */
export const renderPriorityColumn = (item: any, fieldSymbol: string): React.ReactNode => {
  const priorityValue = item?.[fieldSymbol];
  // Jeśli wartość to string, konwertuj na number
  const priority = typeof priorityValue === 'string' ? parseInt(priorityValue, 10) : priorityValue;
  return <WisePriorityComponent priority={priority || 0} />;
};

/**
 * Renderer dla kolumny typu 'avatar' - wyświetla avatar z imieniem i nazwiskiem
 */
export const renderAvatarColumn = (
  item: any,
  fieldSymbol: string,
  _columnAction?: IWiseTableAction,
  _refetch?: () => void,
  column?: IWiseTableColumn
): React.ReactNode => {
  const avatarData = item?.[fieldSymbol];

  if (!avatarData) {
    return null;
  }

  const buildHref = (id: unknown): string | null => {
    if (!column?.link_path || id == null || id === '') return null;
    return column.link_path.replace(':id', String(id));
  };

  // Jeśli avatarData to string, traktuj jako pełne imię i nazwisko
  if (typeof avatarData === 'string') {
    const clientName = avatarData;
    const getInitials = () => {
      const firstPart = clientName.split(' ')[0];
      const secondPart = clientName.split(' ')[1];
      const firstInitial = firstPart?.[0] || '';
      const lastInitial = secondPart?.[0] || firstPart?.[1] || '';
      return `${firstInitial}${lastInitial}`.toUpperCase();
    };

    const linkId = column?.link_id_field ? item?.[column.link_id_field] : null;
    const href = buildHref(linkId);

    return (
      <div className={styles.avatarWrapper}>
        <Avatar className={styles.avatar}>{getInitials()}</Avatar>
        {clientName &&
          (href ? (
            <Link to={href} className={classnames(styles.avatarName, styles.link)}>
              {clientName}
            </Link>
          ) : (
            <span className={styles.avatarName}>{clientName}</span>
          ))}
      </div>
    );
  }

  // Jeśli avatarData to obiekt z name i surname
  if (typeof avatarData === 'object') {
    const firstName = avatarData.name || avatarData.first_name || '';
    const lastName = avatarData.surname || avatarData.last_name || '';

    const getInitials = () => {
      const firstInitial = firstName?.[0] || '';
      const lastInitial = lastName?.[0] || '';
      return `${firstInitial}${lastInitial}`.toUpperCase();
    };

    const fullName = `${firstName} ${lastName}`.trim();

    const linkIdFromColumn = column?.link_id_field
      ? avatarData?.[column.link_id_field] ?? item?.[column.link_id_field]
      : avatarData?.id;
    const href = buildHref(linkIdFromColumn) || avatarData.urlLink || null;

    return (
      <div className={styles.avatarWrapper}>
        <Avatar className={styles.avatar}>{getInitials()}</Avatar>
        {fullName &&
          (href ? (
            <Link to={href} className={classnames(styles.avatarName, styles.link)}>
              {fullName}
            </Link>
          ) : (
            <span className={styles.avatarName}>{fullName}</span>
          ))}
      </div>
    );
  }

  return null;
};

/**
 * Renderer dla kolumny typu 'icon' - wyświetla ikonę na podstawie typu (URL lub BootstrapIcons)
 */
export const renderIconColumn = (item: any, fieldSymbol: string): React.ReactNode => {
  const iconData = item?.[fieldSymbol];
  
  if (!iconData) {
    return null;
  }

  // Jeśli iconData to string (URL), renderuj jako obrazek
  if (typeof iconData === 'string') {
    return (
      <div className={styles.iconWrapper}>
        <img src={iconData} alt="" className={styles.iconImage} />
      </div>
    );
  }

  // Jeśli iconData to obiekt z type i url
  if (typeof iconData === 'object') {
    // Jeśli URL jest podany, użyj obrazka
    if (iconData.url && iconData.url.trim() !== '') {
      return (
        <div className={styles.iconWrapper}>
          <img src={iconData.url} alt={iconData.type || ''} className={styles.iconImage} />
        </div>
      );
    }

    // W przeciwnym razie użyj ikony z bootstrap-icons
    if (iconData.type) {
      const IconComponent = (BootstrapIcons as any)[iconData.type] as React.ComponentType<any>;
      if (IconComponent) {
        return (
          <div className={styles.iconWrapper}>
            <IconComponent className={styles.iconSvg} />
          </div>
        );
      }
    }
  }

  return null;
};

export const renderDateColumn = (
  item: any,
  fieldSymbol: string,
  _columnAction?: IWiseTableAction,
  _refetch?: () => void,
  column?: IWiseTableColumn,
): React.ReactNode => {
  const dateValue = item?.[fieldSymbol];

  if (!dateValue) {
    return null;
  }

  try {
    const date = parseISO(dateValue);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = addDays(today, 1);
    const isPast = isBefore(date, now);
    const highlightPast = Boolean(column?.highlight_past_date);
    const spanClassName = isPast && highlightPast ? styles.datePast : undefined;

    // Sprawdź czy data jest dzisiaj
    if (isSameDay(date, today)) {
      const time = format(date, 'HH:mm');
      return (
        <span className={spanClassName}>
          <Trans>Dzisiaj</Trans>, {time}
        </span>
      );
    }

    // Sprawdź czy data jest jutro
    if (isSameDay(date, tomorrow)) {
      const time = format(date, 'HH:mm');
      return (
        <span className={spanClassName}>
          <Trans>Jutro</Trans>, {time}
        </span>
      );
    }

    // W przeciwnym razie wyświetl normalny format daty
    return <span className={spanClassName}>{format(date, 'dd.MM.yyyy HH:mm')}</span>;
  } catch (error) {
    // Jeśli parsowanie się nie powiodło, zwróć surową wartość
    return <span>{dateValue}</span>;
  }
};

/**
 * Mapa rendererów dla różnych typów kolumn
 */
export const COLUMN_RENDERERS: Record<
  string,
  (
    item: any,
    fieldSymbol: string,
    columnAction?: IWiseTableAction,
    refetch?: () => void,
    column?: IWiseTableColumn,
    requestParams?: object
  ) => React.ReactNode
> = {
  text: renderTextColumn,
  string: renderTextColumn,
  status: renderStatusColumn,
  image: renderImageColumn,
  address: renderAddressColumn,
  boolean: renderBooleanColumn,
  grade: renderGradeColumn,
  priority: renderPriorityColumn,
  avatar: renderAvatarColumn,
  icon: renderIconColumn,
  date: renderDateColumn,
  link: renderLinkColumn
};

/**
 * Domyślny renderer (fallback)
 */
export const defaultRenderer = (item: any, fieldSymbol: string): React.ReactNode => {
  return <span>{item?.[fieldSymbol]}</span>;
};
